import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  LinearProgress,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Skeleton,
  Fade,
  Stack,
  Alert,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TableRestaurant as TableIcon,
  SoupKitchen as KitchenIcon,
  Inventory as InventoryIcon,
  AttachMoney as MoneyIcon,
  PointOfSale as PosIcon,
  ArrowForward as ArrowIcon,
  LocalDining as DiningIcon,
  ShoppingCart as CartIcon,
  Warning as WarningIcon,
  AccessTime as TimeIcon,
  Refresh as RefreshIcon,
  Menu as MenuIcon,
  ReceiptLong as ReceiptIcon,
  Dashboard as DashboardIcon,
  Storefront as StorefrontIcon,
} from '@mui/icons-material';

const Dashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [stats, setStats] = useState({
    todaySales: 0,
    monthSales: 0,
    todayExpenses: 0,
    monthExpenses: 0,
    pendingOrders: 0,
    activeTables: 0,
    totalTables: 0,
    pendingKOTs: 0,
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [topItems, setTopItems] = useState([]);
  const [tables, setTables] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('browser');
  const [error, setError] = useState(null);

  // ==================== SAFE API ACCESS ====================
  const getAPI = useCallback(() => {
    if (typeof window !== 'undefined' && window.electronAPI && window.electronAPI.isElectron === true) {
      return window.electronAPI;
    }
    return null;
  }, []);

  // ==================== STORAGE ACCESS (Browser Mode) ====================
  const getStorage = useCallback(async () => {
    try {
      const module = await import('../database/storage');
      const storage = module.default || module;
      await storage.init();
      return storage;
    } catch (e) {
      console.warn('[Dashboard] Storage not available:', e);
      return null;
    }
  }, []);

  // ==================== FORMAT HELPERS ====================
  const formatCurrency = useCallback((amount) => {
    const val = Number(amount) || 0;
    return `PKR ${val.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  }, []);

  const formatTime = useCallback((timestamp) => {
    if (!timestamp) return '--:--';
    try {
      return new Date(timestamp).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return '--:--';
    }
  }, []);

  const getTableStatusColor = useCallback((status) => {
    const colors = {
      empty: '#4caf50',
      occupied: '#ff9800',
      reserved: '#2196f3',
      billing: '#f44336',
      cleaning: '#9c27b0',
      merged: '#607d8b',
    };
    return colors[status] || '#757575';
  }, []);

  const getTableStatusLabel = useCallback((status) => {
    const labels = {
      empty: 'خالی',
      occupied: 'مشغول',
      reserved: 'ریزروڈ',
      billing: 'بلنگ',
      cleaning: 'صاف',
      merged: 'مرج',
    };
    return labels[status] || status;
  }, []);

  // ==================== LOAD DATA ====================
  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const api = getAPI();
      let summary = {};
      let allTables = [];
      let kots = [];
      let inventory = [];
      let orders = [];
      let menuReport = [];

      // ✅ ELECTRON MODE
      if (api) {
        console.log('[Dashboard] ✅ Electron mode - using electronAPI');
        setMode('electron');

        try {
          summary = await api.getAccountsSummary() || {};
        } catch (e) { console.warn('[Dashboard] Summary error:', e); }

        try {
          allTables = await api.getTables() || [];
        } catch (e) { console.warn('[Dashboard] Tables error:', e); }

        try {
          kots = await api.getPendingKOTs() || [];
        } catch (e) { console.warn('[Dashboard] KOTs error:', e); }

        try {
          inventory = await api.getInventory() || [];
        } catch (e) { console.warn('[Dashboard] Inventory error:', e); }

        try {
          orders = await api.getOrders({}) || [];
        } catch (e) { console.warn('[Dashboard] Orders error:', e); }

        try {
          const today = new Date().toISOString().split('T')[0];
          menuReport = await api.getMenuReport(today, today) || [];
        } catch (e) { console.warn('[Dashboard] Menu report error:', e); }

      } else {
        // ✅ BROWSER MODE
        console.log('[Dashboard] Browser mode - using storage');
        setMode('browser');
        const storage = await getStorage();
        
        if (!storage) {
          throw new Error('Storage not available');
        }

        try {
          summary = await storage.getAccountsSummary() || {};
        } catch (e) { console.warn('[Dashboard] Summary error:', e); }

        try {
          allTables = await storage.getTables() || [];
        } catch (e) { console.warn('[Dashboard] Tables error:', e); }

        try {
          kots = await storage.getPendingKOTs() || [];
        } catch (e) { console.warn('[Dashboard] KOTs error:', e); }

        try {
          inventory = await storage.getInventory() || [];
        } catch (e) { console.warn('[Dashboard] Inventory error:', e); }

        try {
          orders = await storage.getOrders({}) || [];
        } catch (e) { console.warn('[Dashboard] Orders error:', e); }

        try {
          const today = new Date().toISOString().split('T')[0];
          menuReport = await storage.getMenuReport(today, today) || [];
        } catch (e) { console.warn('[Dashboard] Menu report error:', e); }
      }

      // ✅ SET STATS - Always use defaults
      setStats({
        todaySales: Number(summary?.todaySales) || 0,
        monthSales: Number(summary?.monthSales) || 0,
        todayExpenses: Number(summary?.todayExpenses) || 0,
        monthExpenses: Number(summary?.monthExpenses) || 0,
        pendingOrders: Number(summary?.pendingOrders) || 0,
        activeTables: Number(summary?.activeTables) || 0,
        totalTables: allTables?.length || 0,
        pendingKOTs: kots?.length || 0,
      });

      setTables(allTables || []);
      setRecentOrders((orders || []).slice(0, 10));

      // Sanitize top items
      const sanitizedReport = (menuReport || []).map(item => ({
        ...item,
        total_sold: Number(item.total_sold) || 0,
        total_revenue: Number(item.total_revenue) || 0,
      }));
      setTopItems(sanitizedReport.slice(0, 5));

      // Low stock
      const low = (inventory || []).filter(item => 
        Number(item.current_stock) <= Number(item.reorder_point)
      );
      setLowStock(low.slice(0, 5));

    } catch (err) {
      console.error('[Dashboard] Load error:', err);
      setError('Failed to load dashboard data. Please refresh.');
      // Set default values so UI doesn't break
      setStats({
        todaySales: 0,
        monthSales: 0,
        todayExpenses: 0,
        monthExpenses: 0,
        pendingOrders: 0,
        activeTables: 0,
        totalTables: 0,
        pendingKOTs: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [getAPI, getStorage]);

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 30000);
    return () => clearInterval(interval);
  }, [loadDashboardData]);

  // ==================== STAT CARDS ====================
  const statCards = useMemo(() => [
    {
      title: 'Today Sales',
      titleUrdu: 'آج کی فروخت',
      value: formatCurrency(stats.todaySales),
      icon: <MoneyIcon />,
      color: '#2e7d32',
      bgColor: '#e8f5e9',
      path: '/reports',
    },
    {
      title: 'Active Tables',
      titleUrdu: 'مشغول ٹیبل',
      value: `${stats.activeTables} / ${stats.totalTables}`,
      icon: <TableIcon />,
      color: '#ed6c02',
      bgColor: '#fff3e0',
      path: '/tables',
    },
    {
      title: 'Pending KOTs',
      titleUrdu: 'زیر التواء KOT',
      value: stats.pendingKOTs,
      icon: <KitchenIcon />,
      color: '#d32f2f',
      bgColor: '#ffebee',
      path: '/kitchen',
    },
    {
      title: 'Pending Orders',
      titleUrdu: 'زیر التواء آرڈر',
      value: stats.pendingOrders,
      icon: <CartIcon />,
      color: '#0288d1',
      bgColor: '#e3f2fd',
      path: '/orders',
    },
  ], [stats, formatCurrency]);

  // ==================== SKELETON ====================
  const StatSkeleton = () => (
    <Card sx={{ borderLeft: '4px solid #e0e0e0', height: '100%' }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Skeleton variant="circular" width={48} height={48} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="60%" height={32} />
        <Skeleton variant="text" width="40%" height={20} />
      </CardContent>
    </Card>
  );

  // ==================== STAT CARD COMPONENT ====================
  const StatCard = useMemo(() => ({ card }) => (
    <Fade in timeout={300}>
      <Card
        sx={{
          cursor: 'pointer',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: { xs: 'none', sm: 'translateY(-4px)' },
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          },
          borderLeft: `4px solid ${card.color}`,
          height: '100%',
        }}
        onClick={() => navigate(card.path)}
      >
        <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Avatar sx={{ bgcolor: card.bgColor, color: card.color, width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 } }}>
              {card.icon}
            </Avatar>
          </Box>
          <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 700, color: '#1a1a2e', mb: 0.5, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {card.value}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            {card.title}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 0.5, fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
            {card.titleUrdu}
          </Typography>
        </CardContent>
      </Card>
    </Fade>
  ), [isMobile, navigate]);

  // ==================== RENDER ====================
  return (
    <Box sx={{ 
      px: { xs: 1, sm: 2, md: 3 },
      pb: { xs: 8, sm: 3 },
    }}>
      {/* ===== HEADER ===== */}
      <Box sx={{ 
        mb: 3, 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: { xs: 1.5, sm: 0 }
      }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 800, color: '#1a1a2e' }}>
              Dashboard
            </Typography>
            <Chip 
              size="small" 
              label={mode === 'electron' ? 'Desktop' : 'Browser'} 
              sx={{ 
                bgcolor: mode === 'electron' ? '#1a1a2e' : '#e3f2fd',
                color: mode === 'electron' ? '#fff' : '#1976d2',
                fontWeight: 600,
                fontSize: '0.65rem',
                height: 20,
              }} 
            />
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            ڈیش بورڈ | Restaurant Overview
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Tooltip title="Refresh Data">
            <IconButton 
              onClick={loadDashboardData} 
              sx={{ bgcolor: '#f5f5f5', borderRadius: 2 }}
              size={isMobile ? 'small' : 'medium'}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<PosIcon />}
            onClick={() => navigate('/billing')}
            fullWidth={isMobile}
            sx={{
              bgcolor: '#ff6b35',
              '&:hover': { bgcolor: '#e55a2b' },
              fontWeight: 700,
              px: { xs: 2, sm: 3 },
              py: { xs: 0.8, sm: 1 },
              borderRadius: 2,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            }}
            size={isMobile ? 'small' : 'medium'}
          >
            {isMobile ? 'New Order' : 'New Order / نیا آرڈر'}
          </Button>
        </Box>
      </Box>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading Bar */}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1, height: 4 }} />}

      {/* ===== STATS CARDS ===== */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {loading
          ? Array.from({ length: 4 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <StatSkeleton />
              </Grid>
            ))
          : statCards.map((card, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <StatCard card={card} />
              </Grid>
            ))}
      </Grid>

      {/* ===== MAIN CONTENT GRID ===== */}
      <Grid container spacing={2}>
        
        {/* ===== TABLE STATUS OVERVIEW ===== */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ 
            p: { xs: 2, sm: 2.5 }, 
            height: '100%', 
            borderRadius: 3, 
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            border: '1px solid',
            borderColor: 'divider',
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e', fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                  Table Status
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                  ٹیبل کی صورتحال
                </Typography>
              </Box>
              <Button
                size="small"
                endIcon={<ArrowIcon />}
                onClick={() => navigate('/tables')}
                sx={{ color: '#ff6b35', fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
              >
                View All
              </Button>
            </Box>

            {/* Tables Grid */}
            <Box sx={{ 
              display: 'flex',
              flexWrap: { xs: 'nowrap', sm: 'wrap' },
              gap: { xs: 1, sm: 1.5 },
              overflowX: { xs: 'auto', sm: 'visible' },
              pb: { xs: 1, sm: 0 },
              '&::-webkit-scrollbar': { height: 4 },
              '&::-webkit-scrollbar-thumb': { bgcolor: '#ddd', borderRadius: 2 }
            }}>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} variant="rectangular" width={70} height={70} sx={{ borderRadius: 2, flexShrink: 0 }} />
                ))
              ) : tables.length > 0 ? (
                tables.map((table) => (
                  <Tooltip key={table.id} title={`${table.table_number} - ${getTableStatusLabel(table.status)}`}>
                    <Box
                      onClick={() => navigate('/tables')}
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        minWidth: { xs: 60, sm: 'calc(16.66% - 10px)' },
                        flex: { xs: '0 0 auto', sm: '0 0 calc(16.66% - 10px)' },
                        textAlign: 'center',
                        borderRadius: 2,
                        cursor: 'pointer',
                        bgcolor: `${getTableStatusColor(table.status)}15`,
                        border: `2px solid ${getTableStatusColor(table.status)}`,
                        transition: 'all 0.2s',
                        '&:hover': { transform: { sm: 'scale(1.05)' } },
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700, color: getTableStatusColor(table.status), fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                        {table.table_number}
                      </Typography>
                      <Typography variant="caption" sx={{ color: getTableStatusColor(table.status), fontSize: { xs: '0.55rem', sm: '0.65rem' }, display: 'block', mt: 0.3 }}>
                        {getTableStatusLabel(table.status)}
                      </Typography>
                    </Box>
                  </Tooltip>
                ))
              ) : (
                <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
                  No tables found
                </Typography>
              )}
            </Box>

            {/* Legend */}
            <Box sx={{ mt: 2, display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
              {['empty', 'occupied', 'reserved', 'billing', 'cleaning'].map((status) => (
                <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: { xs: 8, sm: 10 }, height: { xs: 8, sm: 10 }, borderRadius: '50%', bgcolor: getTableStatusColor(status) }} />
                  <Typography variant="caption" sx={{ textTransform: 'capitalize', fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                    {status}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* ===== QUICK ACTIONS + LOW STOCK ===== */}
        <Grid item xs={12} md={6} lg={3}>
          <Paper sx={{ 
            p: { xs: 2, sm: 2.5 }, 
            height: '100%', 
            borderRadius: 3, 
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            border: '1px solid',
            borderColor: 'divider',
          }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 0.5, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
              Quick Actions
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
              فوری اقدامات
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
              {[
                { icon: <DiningIcon />, label: 'Table View / ٹیبل', path: '/tables' },
                { icon: <KitchenIcon />, label: 'Kitchen / کچن', path: '/kitchen' },
                { icon: <MenuIcon />, label: 'Menu / مینو', path: '/menu' },
                { icon: <InventoryIcon />, label: 'Inventory / انوینٹری', path: '/inventory' },
              ].map((action, idx) => (
                <Button
                  key={idx}
                  fullWidth
                  variant="outlined"
                  startIcon={action.icon}
                  onClick={() => navigate(action.path)}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{ 
                    justifyContent: 'flex-start', 
                    py: { xs: 1, sm: 1.2 }, 
                    borderRadius: 2,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    fontWeight: 600,
                    textTransform: 'none',
                  }}
                >
                  {action.label}
                </Button>
              ))}
            </Box>

            {lowStock.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#d32f2f', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                  <WarningIcon fontSize="small" /> Low Stock Alert
                </Typography>
                <List dense sx={{ maxHeight: 180, overflow: 'auto' }}>
                  {loading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                      <ListItem key={i} sx={{ px: 0, py: 0.5 }}>
                        <Skeleton variant="text" width="100%" height={24} />
                      </ListItem>
                    ))
                  ) : (
                    lowStock.map((item) => (
                      <ListItem key={item.id} sx={{ px: 0, py: 0.5 }}>
                        <ListItemText
                          primary={item.name}
                          secondary={`${Number(item.current_stock).toFixed(1)} ${item.unit} left`}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.85rem' } }}
                          secondaryTypographyProps={{ variant: 'caption', color: 'error', fontSize: { xs: '0.6rem', sm: '0.7rem' } }}
                        />
                      </ListItem>
                    ))
                  )}
                </List>
              </>
            )}
          </Paper>
        </Grid>

        {/* ===== TOP SELLING ITEMS ===== */}
        <Grid item xs={12} md={6} lg={3}>
          <Paper sx={{ 
            p: { xs: 2, sm: 2.5 }, 
            height: '100%', 
            borderRadius: 3, 
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            border: '1px solid',
            borderColor: 'divider',
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e', fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                  Top Selling
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                  مقبول اشیاء
                </Typography>
              </Box>
              <Chip size="small" label="Today" color="primary" sx={{ fontSize: '0.65rem', height: 24 }} />
            </Box>

            <List sx={{ maxHeight: 280, overflow: 'auto' }}>
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <ListItem key={i} sx={{ px: 0, py: 1 }}>
                    <Skeleton variant="circular" width={36} height={36} sx={{ mr: 2 }} />
                    <Skeleton variant="text" width="70%" height={20} />
                  </ListItem>
                ))
              ) : topItems.length > 0 ? (
                topItems.map((item, index) => (
                  <React.Fragment key={index}>
                    <ListItem sx={{ px: 0, py: 1 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ 
                          bgcolor: index < 3 ? '#ff6b35' : '#e0e0e0', 
                          color: index < 3 ? '#fff' : '#666', 
                          width: 32, height: 32, 
                          fontSize: '0.8rem', fontWeight: 700 
                        }}>
                          {index + 1}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={item.item_name || 'Unknown Item'}
                        secondary={`${item.total_sold} sold • ${formatCurrency(item.total_revenue)}`}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.85rem' } }}
                        secondaryTypographyProps={{ variant: 'caption', fontSize: { xs: '0.6rem', sm: '0.7rem' } }}
                      />
                      <Box sx={{ width: 30, textAlign: 'right' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                          {item.total_sold}
                        </Typography>
                      </Box>
                    </ListItem>
                    {index < topItems.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                ))
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <ReceiptIcon sx={{ color: 'text.disabled', fontSize: 40, mb: 1 }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                    No sales data
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 0.5, fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                    آج کوئی فروخت نہیں
                  </Typography>
                </Box>
              )}
            </List>
          </Paper>
        </Grid>

        {/* ===== RECENT ORDERS ===== */}
        <Grid item xs={12}>
          <Paper sx={{ 
            p: { xs: 2, sm: 2.5 }, 
            borderRadius: 3, 
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            border: '1px solid',
            borderColor: 'divider',
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e', fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                  Recent Orders
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                  حالیہ آرڈرز
                </Typography>
              </Box>
              <Button
                size="small"
                endIcon={<ArrowIcon />}
                onClick={() => navigate('/orders')}
                sx={{ color: '#ff6b35', fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
              >
                View All Orders
              </Button>
            </Box>

            {/* Mobile: Vertical List */}
            {isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {loading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} variant="rectangular" width="100%" height={80} sx={{ borderRadius: 2 }} />
                  ))
                ) : recentOrders.length > 0 ? (
                  recentOrders.map((order) => (
                    <Card
                      key={order.id}
                      sx={{
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.08)' },
                      }}
                      onClick={() => navigate('/orders')}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                            Order #{order.id}
                          </Typography>
                          <Chip
                            size="small"
                            label={order.status || 'pending'}
                            color={order.status === 'billed' ? 'success' : 'warning'}
                            sx={{ fontSize: '0.6rem', height: 22 }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            Table {order.table_number || 'N/A'} • {order.order_type || 'dine_in'}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '0.85rem' }}>
                            {formatCurrency(order.final_amount || order.total_amount || 0)}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.65rem' }}>
                          {formatTime(order.billed_at || order.created_at)} • {order.payment_method || 'cash'}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
                    No recent orders found
                  </Typography>
                )}
              </Box>
            ) : (
              // Desktop: Horizontal Cards
              <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{ display: 'flex', gap: 2, minWidth: 800 }}>
                  {loading ? (
                    Array.from({ length: 4 }).map((_, i) => (
                      <Skeleton key={i} variant="rectangular" width={280} height={160} sx={{ borderRadius: 2, flexShrink: 0 }} />
                    ))
                  ) : recentOrders.length > 0 ? (
                    recentOrders.map((order) => (
                      <Card
                        key={order.id}
                        sx={{
                          minWidth: 260,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          flexShrink: 0,
                          '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.1)' },
                        }}
                        onClick={() => navigate('/orders')}
                      >
                        <CardContent sx={{ p: 2.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a1a2e', fontSize: '0.9rem' }}>
                              Order #{order.id}
                            </Typography>
                            <Chip
                              size="small"
                              label={order.status || 'pending'}
                              color={order.status === 'billed' ? 'success' : 'warning'}
                              sx={{ fontSize: '0.6rem', height: 22 }}
                            />
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <TableIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                              Table {order.table_number || 'N/A'} • {order.order_type || 'dine_in'}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <TimeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              {formatTime(order.billed_at || order.created_at)}
                            </Typography>
                          </Box>

                          <Divider sx={{ my: 1 }} />

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '0.9rem' }}>
                              {formatCurrency(order.final_amount || order.total_amount || 0)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                              {order.payment_method || 'cash'}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <Typography variant="body2" sx={{ color: 'text.secondary', py: 4, textAlign: 'center', width: '100%' }}>
                      No recent orders found
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* ===== MONTHLY SUMMARY BAR ===== */}
        <Grid item xs={12}>
          <Paper sx={{ 
            p: { xs: 2, sm: 2.5 }, 
            borderRadius: 3, 
            bgcolor: '#1a1a2e', 
            color: '#fff',
            border: '1px solid',
            borderColor: 'rgba(255,255,255,0.05)',
          }}>
            <Grid container spacing={{ xs: 2, md: 3 }} alignItems="center">
              <Grid item xs={12} md={4}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 700, mb: 0.5, fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                  Monthly Overview
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                  ماہانہ جائزہ
                </Typography>
              </Grid>
              <Grid item xs={6} md={2} sx={{ textAlign: 'center' }}>
                <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 700, color: '#4caf50', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {formatCurrency(stats.monthSales)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                  Total Sales
                </Typography>
              </Grid>
              <Grid item xs={6} md={2} sx={{ textAlign: 'center' }}>
                <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 700, color: '#f44336', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {formatCurrency(stats.monthExpenses)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                  Total Expenses
                </Typography>
              </Grid>
              <Grid item xs={6} md={2} sx={{ textAlign: 'center' }}>
                <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 700, color: '#ff6b35', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {formatCurrency(Math.max(0, stats.monthSales - stats.monthExpenses))}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                  Net Profit
                </Typography>
              </Grid>
              <Grid item xs={6} md={2} sx={{ textAlign: 'center' }}>
                <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 700, color: stats.monthSales > 0 ? '#4caf50' : '#f44336', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {stats.monthSales > 0 ? ((Math.max(0, stats.monthSales - stats.monthExpenses) / stats.monthSales) * 100).toFixed(1) : 0}%
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                  Profit Margin
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

      </Grid>
    </Box>
  );
};

export default Dashboard;